// Confidence Calibration — Tracks predicted vs actual accuracy by confidence level
// Multi-cert: calibration is USER-GLOBAL (not cert-scoped)
// Uses globalPath() for Firebase persistence

import { globalPath, read, write } from './firebase-store.js';

// In-memory cache: { [userId]: { matrix, totalAnswers } }
const _cache = {};

const CONFIDENCE_LEVELS = ['guessing', 'unsure', 'confident'];

/**
 * Load calibration matrix from Firebase (or cache).
 * Uses globalPath — calibration is a user-level trait, not cert-scoped.
 */
async function _loadMatrix(userId) {
  if (_cache[userId]) return _cache[userId];

  let data = null;
  try {
    data = await read(globalPath(userId, 'confidenceMatrix'));
  } catch (e) {
    // Firebase not initialized — in-memory only
  }

  const matrix = data || {
    guessing:  { correct: 0, total: 0 },
    unsure:    { correct: 0, total: 0 },
    confident: { correct: 0, total: 0 }
  };

  _cache[userId] = matrix;
  return matrix;
}

/**
 * Save calibration matrix to Firebase and cache.
 * Uses globalPath — user-level, not cert-scoped.
 */
async function _saveMatrix(userId, matrix) {
  _cache[userId] = matrix;
  try {
    await write(globalPath(userId, 'confidenceMatrix'), matrix);
  } catch (e) {
    // Firebase not initialized — in-memory only
  }
}

/**
 * Update calibration matrix after an answer.
 * This is user-global — not cert-scoped.
 *
 * @param {string} userId
 * @param {string} confidence - 'guessing' | 'unsure' | 'confident'
 * @param {boolean} correct
 * @returns {object} { confidence, correct, accuracy, calibrationScore }
 */
export async function updateCalibration(userId, confidence, correct) {
  const level = CONFIDENCE_LEVELS.includes(confidence) ? confidence : 'unsure';
  const matrix = await _loadMatrix(userId);

  if (!matrix[level]) matrix[level] = { correct: 0, total: 0 };
  matrix[level].total++;
  if (correct) matrix[level].correct++;

  await _saveMatrix(userId, matrix);

  const accuracy = matrix[level].total > 0
    ? matrix[level].correct / matrix[level].total
    : 0;

  return {
    confidence: level,
    correct,
    accuracy: Math.round(accuracy * 1000) / 1000,
    calibrationScore: computeCalibrationScore(matrix)
  };
}

/**
 * Compute calibration score (0.0 = perfectly calibrated, 1.0 = worst).
 * Uses Expected Calibration Error (ECE).
 */
export function computeCalibrationScore(matrix) {
  const expected = { guessing: 0.25, unsure: 0.50, confident: 0.85 };
  let totalAnswers = 0;
  let weightedError = 0;

  for (const level of CONFIDENCE_LEVELS) {
    const cell = matrix[level];
    if (!cell || cell.total === 0) continue;

    const accuracy = cell.correct / cell.total;
    const error = Math.abs(accuracy - expected[level]);
    weightedError += error * cell.total;
    totalAnswers += cell.total;
  }

  if (totalAnswers === 0) return null;

  const ece = weightedError / totalAnswers;
  return Math.round((1 - ece) * 1000) / 1000;
}

/**
 * Get calibration score (user-global).
 */
export async function getCalibrationScore(userId) {
  const matrix = await _loadMatrix(userId);
  return computeCalibrationScore(matrix);
}

/**
 * Detect "confidently wrong" patterns.
 */
export async function detectConfidentlyWrong(userId) {
  const matrix = await _loadMatrix(userId);
  const cell = matrix.confident;
  if (!cell || cell.total < 5) return [];

  const accuracy = cell.correct / cell.total;
  if (accuracy < 0.5) {
    return [{ type: 'confidently_wrong', accuracy: Math.round(accuracy * 100), total: cell.total }];
  }
  return [];
}
